import React from 'react'
import cn, { Argument as ClassName } from 'classnames'
import { FormProvider, SubmitHandler } from 'react-hook-form'
import { useSelector } from 'react-redux'

import { Button } from '../../../common/components/Button'
import ChargePointDetailsAdminInputs from '../../../common/features/chargepoint-details-widget/chargepoint-details-admin-inputs'
import { notifyError, notifySuccess } from '../../../common/features/notificationsManager/Notify'
import { IChargePoint } from '../../../common/hooks/useChargePoint'
import {
    ChargePoint,
    useChargePointUpdateChargerInformationMutation,
    usePartnerGetExternalServersQuery,
} from '../../../common/redux/api/web-api'
import { mapFormValuesToChargerInformation } from '../../../common/utils/objectMappers'
import { isAdminDomain, isPartnerDomain, isSupportDomain } from '../../../lib/currentDomain'
import useText from '../../../common/hooks/useText'
import Text from '../../../ui/Text'
import { WidgetLayout } from '../../../lib/widget-layout'
import ChargePointDetailsPartnerInputs from '../../../common/features/chargepoint-details-widget/chargepoint-details-partner-inputs'
import { SButtonsGroupContainer } from '../../../common/styles/Widget'
import { RootState } from '../../../partner/redux/store'
import {
    IChargerInformationFormInput,
    useChargerInfoValidation,
} from '../../../common/hooks/useChargePointInfoValidation'

import ChargePointDetailsSupportCapiInputs from './charge-point-capi-support-inputs'

export type ChargePointCapiDetailsWidgetProps = {
    chargePoint: IChargePoint | ChargePoint
    loading: boolean
    className?: ClassName
    style?: React.CSSProperties
}

export function ChargePointCapiDetailsWidget({
    chargePoint,
    loading,
    className,
    style,
}: ChargePointCapiDetailsWidgetProps): React.ReactElement {
    const text = useText()
    const partnerId = useSelector((state: RootState) => state.user?.partnerId)
    const { data: getExternalServerForPartner } = usePartnerGetExternalServersQuery(
        { partnerId: partnerId ?? 0 },
        { skip: !partnerId }
    )

    const [updateChargerInfo, { isLoading: chargerInfoUpdating }] = useChargePointUpdateChargerInformationMutation()

    const chargerInfoFormValidation = useChargerInfoValidation(chargePoint)
    const {
        handleSubmit,
        reset,
        formState: { isDirty },
    } = chargerInfoFormValidation

    const saveChargePoint: SubmitHandler<IChargerInformationFormInput> = (formValues) => {
        updateChargerInfo({
            chargePointId: chargePoint.id,
            chargerInformation: mapFormValuesToChargerInformation(formValues),
        })
            .unwrap()
            .then(() => {
                notifySuccess(text('msgChargerUpdated'))
                reset(formValues, {
                    keepDirty: false,
                    keepDirtyValues: false,
                })
            })
            .catch(() => notifyError(text('msgChargePointNotUpdated')))
    }

    return (
        <WidgetLayout
            title={<Text code="lblChargerInformation" />}
            loadingStatus={loading}
            className={cn(className)}
            style={style}
            data-testid="details"
        >
            <FormProvider {...chargerInfoFormValidation}>
                <form onSubmit={handleSubmit(saveChargePoint)}>
                    {isAdminDomain && <ChargePointDetailsAdminInputs chargePoint={chargePoint} />}
                    {isPartnerDomain && (
                        <ChargePointDetailsPartnerInputs
                            chargePoint={chargePoint}
                            externalServersForPartner={getExternalServerForPartner}
                        />
                    )}
                    {isSupportDomain && <ChargePointDetailsSupportCapiInputs chargePoint={chargePoint} />}
                    <SButtonsGroupContainer className="action-btns">
                        <Button
                            label={text('btnSave')}
                            type="submit"
                            disabled={loading || chargerInfoUpdating || !isDirty}
                            data-testid="save-btn"
                            size="sm"
                        />
                    </SButtonsGroupContainer>
                </form>
            </FormProvider>
        </WidgetLayout>
    )
}
